import type { Component } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import type { RoleCode } from '@/api/types'

declare module 'vue-router' {
  interface RouteMeta {
    /** i18n key */
    titleKey?: string
    icon?: Component
    /** 需要的权限码（满足其一即可） */
    permission?: string | string[]
    /** 需要的角色（满足其一即可） */
    roles?: RoleCode[]
    /** 免登录访问 */
    public?: boolean
    /** 不出现在菜单/面包屑/页签 */
    hidden?: boolean
    /** 页签固定不可关闭 */
    affix?: boolean
    /** 菜单高亮路径（详情页指向列表） */
    activeMenu?: string
    keepAlive?: boolean
  }
}

/** 常量路由（免登录或错误页） */
export const constantRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/Login.vue'),
    meta: { titleKey: 'route.login', public: true, hidden: true },
  },
  {
    path: '/403',
    name: 'Forbidden',
    component: () => import('@/views/error/403.vue'),
    meta: { titleKey: 'route.forbidden', hidden: true },
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: { titleKey: 'route.notFound', hidden: true },
  },
]

/** 布局路由（需登录） */
export const layoutRoute: RouteRecordRaw = {
  path: '/',
  component: () => import('@/layouts/AdminLayout.vue'),
  redirect: '/dashboard',
  children: [
    {
      path: 'dashboard',
      name: 'Dashboard',
      component: () => import('@/views/dashboard/Dashboard.vue'),
      meta: { titleKey: 'route.dashboard', affix: true, permission: 'admin:dashboard:view' },
    },
    {
      path: 'user',
      name: 'User',
      redirect: '/user/list',
      meta: { titleKey: 'route.user' },
      children: [
        {
          path: 'list',
          name: 'UserList',
          component: () => import('@/views/user/UserList.vue'),
          meta: { titleKey: 'route.userList', permission: 'admin:user:list' },
        },
        {
          path: 'detail/:id',
          name: 'UserDetail',
          component: () => import('@/views/user/UserDetail.vue'),
          meta: {
            titleKey: 'route.userDetail',
            hidden: true,
            activeMenu: '/user/list',
            permission: 'admin:user:list',
            noCache: true,
          },
        },
      ],
    },
    {
      path: 'wordbook',
      name: 'Wordbook',
      redirect: '/wordbook/list',
      meta: { titleKey: 'route.wordbook' },
      children: [
        {
          path: 'list',
          name: 'WordbookList',
          component: () => import('@/views/wordbook/WordbookList.vue'),
          meta: { titleKey: 'route.wordbookList', permission: 'admin:wordbook:list' },
        },
        {
          path: 'import',
          name: 'WordbookImport',
          component: () => import('@/views/wordbook/WordImport.vue'),
          meta: { titleKey: 'route.wordbookImport', permission: 'admin:wordbook:import' },
        },
      ],
    },
    {
      path: 'word',
      name: 'Word',
      redirect: '/word/list',
      meta: { titleKey: 'route.word' },
      children: [
        {
          path: 'list',
          name: 'WordList',
          component: () => import('@/views/word/WordList.vue'),
          meta: { titleKey: 'route.wordList', permission: 'admin:word:list' },
        },
        {
          path: 'import',
          name: 'WordImport',
          component: () => import('@/views/word/WordImport.vue'),
          meta: { titleKey: 'route.wordImport', permission: 'admin:word:import' },
        },
      ],
    },
    {
      path: 'dictionary',
      name: 'Dictionary',
      redirect: '/dictionary/list',
      meta: { titleKey: 'route.dictionary' },
      children: [
        {
          path: 'list',
          name: 'DictionaryList',
          component: () => import('@/views/dictionary/DictionaryList.vue'),
          meta: { titleKey: 'route.dictionaryList', permission: 'admin:dictionary:list' },
        },
        {
          path: 'detail/:id',
          name: 'DictionaryDetail',
          component: () => import('@/views/dictionary/DictionaryDetail.vue'),
          meta: {
            titleKey: 'route.dictionaryDetail',
            hidden: true,
            activeMenu: '/dictionary/list',
            permission: 'admin:dictionary:list',
            noCache: true,
          },
        },
      ],
    },
    {
      path: 'system',
      name: 'System',
      redirect: '/system/config',
      meta: { titleKey: 'route.system' },
      children: [
        {
          path: 'config',
          name: 'SystemConfig',
          component: () => import('@/views/system/ConfigList.vue'),
          meta: { titleKey: 'route.systemConfig', permission: 'admin:system:config:list' },
        },
        {
          path: 'log',
          name: 'SystemLog',
          component: () => import('@/views/system/AdminLog.vue'),
          meta: { titleKey: 'route.systemLog', permission: 'admin:system:log:list' },
        },
        {
          path: 'profile',
          name: 'SystemProfile',
          component: () => import('@/views/system/Profile.vue'),
          meta: { titleKey: 'route.systemProfile' },
        },
      ],
    },
  ],
}

/** 兜底路由 */
export const fallbackRoute: RouteRecordRaw = {
  path: '/:pathMatch(.*)*',
  redirect: '/404',
}

export const routes: RouteRecordRaw[] = [...constantRoutes, layoutRoute, fallbackRoute]

export default routes
